import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import MarketingShell from "@/components/MarketingShell";
import PatronageDialog from "@/components/PatronageDialog";
import { PHOTOS } from "@/lib/photos";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


const EASE = [0.4, 0, 0.2, 1];

const reveal = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
};

const TIERS = [
  {
    key: "fan",
    price: "$5",
    label: "Fan",
    line: "Your name on the wall, in good company.",
    nameSize: "text-lg",
  },
  {
    key: "patron",
    price: "$18",
    label: "Patron",
    line: "Chai. Your name, bigger.",
    featured: true,
    nameSize: "text-3xl",
  },
  {
    key: "producer",
    price: "$36",
    label: "Producer",
    line: "Double chai. Your name, biggest.",
    nameSize: "text-5xl",
  },
];

const PERKS = [
  {
    title: "Your name on this page",
    body: "The Patrons wall, right here, pulled live from the membership — first name, last initial, and where you're from. Producers sit at the top and largest, Patrons next, Fans after.",
  },
  {
    title: "Credit on every album",
    body: "Any album released while you're an active patron carries your name in the credits. Not a mailing-list footnote — the actual record.",
  },
  {
    title: "A shoutout on the podcast",
    body: "New patrons get thanked by name on Dan's next podcast episode.",
  },
  {
    title: "The behind-the-scenes WhatsApp",
    body: "Dan's patrons-only community: voice notes from the piano, works in progress, half-finished ideas, and the occasional 'which one of these is better?'",
  },
];

const goldBtn =
  "inline-flex items-center justify-center rounded-full px-6 h-12 text-xs uppercase tracking-[0.22em] transition-colors";
const plainCta =
  "inline-flex items-center justify-center rounded-full px-6 h-12 text-xs uppercase tracking-[0.22em] border border-white/25 transition-colors hover:bg-white/10";

const TIER_STYLES = {
  producer: { size: "clamp(28px, 3.4vw, 46px)", color: "#F2D589", opacity: 1, pad: "1rem 1.6rem", tilt: -2.2 },
  patron: { size: "clamp(20px, 2.2vw, 30px)", color: "#FFD9C2", opacity: 0.97, pad: "0.75rem 1.25rem", tilt: 1.8 },
  fan: { size: "clamp(15px, 1.4vw, 19px)", color: "rgba(245,239,227,0.86)", opacity: 0.94, pad: "0.55rem 1rem", tilt: -1.4 },
};

function Wall({ patrons, onJoin }) {
  if (patrons === null) {
    return (
      <p className="text-sm text-center" style={{ color: "rgba(245,239,227,0.6)" }} data-testid="patrons-loading">
        Reading the wall…
      </p>
    );
  }
  if (patrons.length === 0) {
    return (
      <div
        className="rounded-2xl border px-6 py-12 sm:px-10 sm:py-16 text-center backdrop-blur-md"
        style={{ borderColor: "rgba(255,255,255,0.12)", backgroundColor: "rgba(8,4,30,0.45)" }}
        data-testid="patrons-empty"
      >
        <p
          className="text-2xl sm:text-3xl"
          style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500, fontStyle: "italic" }}
        >
          The wall is empty. Somebody has to be first.
        </p>
        <div className="mt-8">
          <button
            type="button"
            onClick={onJoin}
            className={goldBtn}
            style={{ backgroundColor: "#F2D589", color: "#080419", fontWeight: 600 }}
            data-testid="patrons-empty-cta"
          >
            Be the first name &rarr;
          </button>
        </div>
      </div>
    );
  }

  return (
    <ul
      className="flex flex-wrap items-center justify-center gap-x-6 gap-y-7"
      data-testid="patrons-wall"
    >
      {patrons.map((p, i) => {
        const t = TIER_STYLES[p.tier] || TIER_STYLES.fan;
        const tilt = t.tilt + ((i % 3) - 1) * 1.3;
        return (
          <motion.li
            key={p.id}
            initial={{ opacity: 0, y: 12, rotate: tilt }}
            whileInView={{ opacity: t.opacity, y: 0, rotate: tilt }}
            whileHover={{ rotate: 0, scale: 1.04 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.6, delay: Math.min(i * 0.03, 0.5) }}
            className="relative text-center rounded-[3px]"
            style={{
              padding: t.pad,
              // Fridge-magnet slab: warm plastic face, hard shadow on the wall.
              background: "linear-gradient(160deg, rgba(255,247,220,0.14) 0%, rgba(242,213,137,0.07) 55%, rgba(8,4,25,0.35) 100%)",
              border: "1px solid rgba(242,213,137,0.4)",
              boxShadow:
                "0 10px 22px -8px rgba(0,0,0,0.75), inset 0 1px 0 rgba(255,247,220,0.35), inset 0 -1px 0 rgba(8,4,25,0.6)",
            }}
            data-testid={`patron-${p.tier}`}
          >
            <span
              className="block"
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontWeight: 500,
                fontSize: t.size,
                color: t.color,
                lineHeight: 1.15,
                textShadow: "0 1px 0 rgba(8,4,25,0.7)",
              }}
            >
              {p.name}
            </span>
            {p.region ? (
              <span
                className="block text-[10px] uppercase tracking-[0.24em] mt-1"
                style={{ color: "rgba(245,239,227,0.5)", fontFamily: "'Courier Prime', monospace" }}
              >
                {p.region}
              </span>
            ) : null}
          </motion.li>
        );
      })}
    </ul>
  );
}

export default function Patrons() {
  const [patrons, setPatrons] = useState(null);
  const [total, setTotal] = useState(0);
  const [joinTier, setJoinTier] = useState(null);
  const openJoin = (tier) => setJoinTier(tier);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/patrons`)
      .then(({ data }) => {
        if (!alive) return;
        setPatrons(data.patrons || []);
        setTotal(data.total || 0);
      })
      .catch(() => {
        if (alive) setPatrons([]);
      });
    return () => {
      alive = false;
    };
  }, []);

  return (
    <MarketingShell active="/patrons">
      <PatronageDialog
        open={joinTier !== null}
        onOpenChange={(v) => setJoinTier(v ? joinTier : null)}
        initialTier={joinTier || "patron"}
      />
      <motion.section
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: EASE }}
        data-testid="patrons-hero"
      >
        <p className="text-[10px] uppercase tracking-[0.32em] mb-3" style={{ color: "#F2D589" }}>
          Patrons
        </p>
        <h1
          className="leading-[0.95]"
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontWeight: 500,
            fontSize: "clamp(40px, 5.4vw, 78px)",
            textWrap: "balance",
            maxWidth: "22ch",
          }}
          data-testid="patrons-headline"
        >
          Support the music<span style={{ color: "#FCA07A" }}>.</span>
        </h1>
        <p className="mt-6 max-w-[46rem] text-base sm:text-lg leading-relaxed" style={{ color: "rgba(245,239,227,0.82)" }}>
          There&apos;s no label behind this and no grant. Just a piano, a
          method, and people who want more of it to exist. Support the music for
          $5, $18 or $36 a month — or name your own amount — and you&apos;re
          part of how the next album gets made.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => openJoin("patron")}
            className={goldBtn}
            style={{ backgroundColor: "#F2D589", color: "#080419", fontWeight: 600, boxShadow: "0 0 40px rgba(242,213,137,0.3)" }}
            data-testid="patrons-hero-cta"
          >
            Become a patron &rarr;
          </button>
          <a href="#wall" className={plainCta} data-testid="patrons-hero-wall">
            See the wall
          </a>
        </div>
        {total > 0 ? (
          <p className="mt-6 text-sm" style={{ color: "#F2D589" }} data-testid="patrons-count">
            {total} {total === 1 ? "person is" : "people are"} keeping this music going right now.
          </p>
        ) : null}
      </motion.section>

      {/* Tiers */}
      <section className="mt-20" data-testid="patrons-tiers">
        <h2 className="text-3xl sm:text-4xl" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
          Pick a size.
        </h2>
        <p className="mt-3 max-w-[48rem] text-sm sm:text-base" style={{ color: "rgba(245,239,227,0.7)", lineHeight: 1.75 }}>
          Every tier gets everything. The honest truth: the only real difference
          is how big your name is on the wall below — and how much you&apos;re
          helping keep the music flowing, especially my weekly{" "}
          <Link
            to="/podcast"
            className="underline underline-offset-4 hover:text-[#F2D589] transition-colors"
            style={{ color: "#FCA07A" }}
            data-testid="patrons-podcast-link"
          >
            Alephonics podcast
          </Link>
          .
        </p>

        <ul className="mt-10 grid md:grid-cols-3 gap-5">
          {TIERS.map((t, i) => (
            <motion.li
              key={t.key}
              {...reveal}
              transition={{ duration: 0.7, ease: EASE, delay: i * 0.08 }}
              className="flex"
            >
              <div
                className="flex h-full w-full flex-col rounded-2xl border p-6 sm:p-7 backdrop-blur-md"
                style={{
                  borderColor: t.featured ? "rgba(242,213,137,0.55)" : "rgba(255,255,255,0.12)",
                  backgroundColor: t.featured ? "rgba(12,8,38,0.75)" : "rgba(8,4,30,0.45)",
                }}
                data-testid={`patrons-tier-${t.key}`}
              >
                <p className="text-[10px] uppercase tracking-[0.3em]" style={{ color: "#FCA07A" }}>
                  {t.label}
                </p>
                <p
                  className="mt-3 flex items-baseline gap-2"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
                >
                  <span style={{ fontSize: "clamp(38px, 4vw, 56px)", color: "#F2D589", lineHeight: 1 }}>
                    {t.price}
                  </span>
                  <span className="text-sm" style={{ color: "rgba(245,239,227,0.6)" }}>
                    / month
                  </span>
                </p>
                <p className="mt-4 text-sm leading-relaxed" style={{ color: "rgba(245,239,227,0.75)" }}>
                  {t.line}
                </p>
                <p
                  className={`mt-6 ${t.nameSize}`}
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500, color: "rgba(245,239,227,0.55)" }}
                >
                  Your Name Here
                </p>
                <button
                  type="button"
                  onClick={() => openJoin(t.key)}
                  className={`${goldBtn} mt-auto pt-0`}
                  style={
                    t.featured
                      ? { backgroundColor: "#F2D589", color: "#080419", fontWeight: 600, marginTop: "1.75rem" }
                      : {
                          color: "#F2D589",
                          border: "1px solid rgba(242,213,137,0.5)",
                          marginTop: "1.75rem",
                        }
                  }
                  data-testid={`patrons-tier-cta-${t.key}`}
                >
                  Become a {t.label}
                </button>
              </div>
            </motion.li>
          ))}
        </ul>
        <p className="mt-6 text-sm" style={{ color: "rgba(245,239,227,0.7)" }}>
          Something else in mind?{" "}
          <button
            type="button"
            onClick={() => openJoin("custom")}
            className="underline underline-offset-4 hover:text-[#F2D589] transition-colors"
            style={{ color: "#FCA07A" }}
            data-testid="patrons-custom-amount"
          >
            Name your own monthly amount
          </button>
          {" "}— anything from $2 to $1,000 a month, and the tier follows the amount.
        </p>
      </section>

      {/* Perks */}
      <section className="mt-24" data-testid="patrons-perks">
        <p className="text-[10px] uppercase tracking-[0.32em] mb-3" style={{ color: "#F2D589" }}>
          What every patron gets
        </p>
        <h2 className="text-3xl sm:text-4xl leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
          Four things, whichever tier you pick.
        </h2>

        <div className="mt-10 grid md:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div
            className="rounded-2xl overflow-hidden border hidden md:block"
            style={{ borderColor: "rgba(255,255,255,0.12)", aspectRatio: "4 / 5" }}
          >
            <img src={PHOTOS.danHands} alt="Hands on the keys" className="w-full h-full object-cover" loading="lazy" />
          </div>
          <ul className="grid gap-8">
          {PERKS.map((p, i) => (
            <motion.li
              key={p.title}
              {...reveal}
              transition={{ duration: 0.7, ease: EASE, delay: i * 0.07 }}
              className="pl-6"
              style={{ borderLeft: "1px solid rgba(242,213,137,0.3)" }}
              data-testid={`patrons-perk-${i}`}
            >
              <h3 className="text-2xl" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
                {p.title}
              </h3>
              <p className="mt-2 text-sm sm:text-base leading-relaxed" style={{ color: "rgba(245,239,227,0.72)" }}>
                {p.body}
              </p>
            </motion.li>
            ))}
          </ul>
        </div>
      </section>

      {/* The wall */}
      <section id="wall" className="mt-24 scroll-mt-24" data-testid="patrons-wall-section">
        <p className="text-[10px] uppercase tracking-[0.32em] mb-3 text-center" style={{ color: "#F2D589" }}>
          The Patrons
        </p>
        <h2
          className="text-3xl sm:text-4xl text-center mb-10"
          style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
        >
          The people keeping this going.
        </h2>
        <Wall patrons={patrons} onJoin={() => openJoin("patron")} />
      </section>

      <section
        className="mt-24 rounded-2xl border p-6 sm:p-10 text-center backdrop-blur-md"
        style={{ borderColor: "rgba(242,213,137,0.3)", backgroundColor: "rgba(12,8,38,0.6)" }}
        data-testid="patrons-footer-cta"
      >
        <h2 className="text-2xl sm:text-3xl" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
          Put your name on it.
        </h2>
        <p className="mt-3 max-w-[38rem] mx-auto text-sm sm:text-base" style={{ color: "rgba(245,239,227,0.72)" }}>
          Cancel any time, no hard feelings. While you&apos;re in, the music has
          your name on it.
        </p>
        <div className="mt-7 flex flex-wrap justify-center gap-3">
          <button
            type="button"
            onClick={() => openJoin("patron")}
            className={goldBtn}
            style={{ backgroundColor: "#F2D589", color: "#080419", fontWeight: 600 }}
            data-testid="patrons-footer-join"
          >
            Become a patron &rarr;
          </button>
          <Link to="/music" className={plainCta} data-testid="patrons-footer-listen">
            Listen first
          </Link>
        </div>
      </section>
    </MarketingShell>
  );
}
